/**
 *  The main panel for editing global mobile application styles.
 */
import React, { useContext, useState, useEffect } from 'react';
import { WizardContext } from './hooks/WizardContext';
import PanelHeader from './PanelHeader';
import fontService from 'FontService';

import * as S from './styles/StylingPanel.styles';
import FontStylerRow from './FontStylerRow';

const StylingPanel = ({ onClose }) => {

    const { globalStyles, setGlobalStyles } = useContext(WizardContext);
    const availableFonts = [...fontService.getFonts()];
    availableFonts.sort((a, b) => {
        const nameA = a.name; 
        const nameB = b.name;
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });

    const [localWidth, setLocalWidth] = useState(globalStyles?.maxAppWidth || '');
    const [localMargin, setLocalMargin] = useState(globalStyles?.marginSpacing || '');

    useEffect(() => {
        if (globalStyles?.maxAppWidth !== undefined) {
            setLocalWidth(globalStyles.maxAppWidth);
        }
    }, [globalStyles?.maxAppWidth]);

    useEffect(() => {
        if (globalStyles?.marginSpacing !== undefined) {
            setLocalMargin(globalStyles.marginSpacing);
        }
    }, [globalStyles?.marginSpacing]);

    // Handler to commit local state changes to global state on blur
    const handleBlur = (key, value) => {
        const numericValue = parseInt(value, 10);
        setGlobalStyles(prev => ({
            ...prev,
            [key]: isNaN(numericValue) ? (key === 'maxAppWidth' ? 450 : 0) : numericValue
        }));
    };

    // Handler for numeric input changes, filtering non-digits
    const handleNumericChange = (value, setter) => {
        const numericValue = value.replace(/\D/g, '');
        setter(numericValue);
    };

    // Handler for font family changes
    const handleFontFamilyChange = (key, value) => {
        const font = availableFonts.find(font => font.value === value);
        fontService.loadWebFontResource(font);
        setGlobalStyles(prev => ({ ...prev, [key]: value }));
    };

    // Handler for nested font styling changes
    const handleFontChange = (controlKey, styleKey, value) => {
        setGlobalStyles(prev => ({
            ...prev,
            fontStyling: {
                ...prev.fontStyling,
                [controlKey]: {
                    ...prev.fontStyling[controlKey],
                    [styleKey]: value,
                }
            }
        }));
    };

    if (!globalStyles) {
        return <div>Loading styles...</div>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <PanelHeader title="App Styling" showCloseButton={true} onClose={onClose} />
            <S.PanelBody>
                <S.StyleGroup>
                    <S.SettingsRow>
                        <S.InputGroup>
                            <S.Label>Max App Width</S.Label>
                            <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={localWidth || ''}
                                onChange={e => handleNumericChange(e.target.value, setLocalWidth)}
                                onBlur={() => handleBlur('maxAppWidth', localWidth)}
                                style={{ width: '100%', padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px' }}
                            />
                        </S.InputGroup>
                        <S.InputGroup>
                            <S.Label>Margin Spacing</S.Label>
                            <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={localMargin || ''}
                                onChange={e => handleNumericChange(e.target.value, setLocalMargin)}
                                onBlur={() => handleBlur('marginSpacing', localMargin)}
                                style={{ width: '100%', padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px' }}
                            />
                        </S.InputGroup>
                    </S.SettingsRow>
                </S.StyleGroup>

                <S.StyleGroup>
                    <S.GroupTitle>Font Family</S.GroupTitle>
                    <S.Select value={globalStyles.fontFamily || ''} onChange={e => handleFontFamilyChange('fontFamily', e.target.value)}>
                        {availableFonts.map(font => (
                            <option key={font.name} value={font.value}>{font.name}</option>
                        ))}
                    </S.Select>
                </S.StyleGroup>

                <S.StyleGroup>
                    <S.GroupTitle>Font Styling</S.GroupTitle>
                    {Object.entries(globalStyles.fontStyling)
                        .filter(([key, styles]) => styles.show)
                        .map(([key, styles]) => (
                            <FontStylerRow
                                key={key}
                                controlKey={key}
                                styles={styles}
                                onFontChange={handleFontChange}
                            />
                        ))}
                </S.StyleGroup>
            </S.PanelBody>
        </div>
    );
};

export default StylingPanel;